import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Axios from "../../_services/caller.service";
import styles from "./UpComingCalibrations.module.scss";

const UpcomingCalibrations = () => {
  const [calibrations, setCalibrations] = useState([]);
  const [error, setError] = useState(null);
  const [minDays, setMinDays] = useState(-360);
  const [maxDays, setMaxDays] = useState(30);
  const [selectedPeriod, setSelectedPeriod] = useState("1month");
  const [selectedTransducer, setSelectedTransducer] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [showDetails, setShowDetails] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { t } = useTranslation();

  const transducerNames = {
    Bone: "Conduction Osseuse",
    Inserts: "Inserts",
    Headphones: "Casque",
    Speakers: "Champ Libre",
  };

  const periodOptions = {
    "1week": 7,
    "2weeks": 14,
    "1month": 30,
    "3months": 90,
    "6months": 180,
  };

  useEffect(() => {
    fetchCalibrations();
  }, []);

  const fetchCalibrations = async () => {
    setError(null);
    try {
      const response = await Axios.get(
        `/getUpcomingCalibrations?minDays=${minDays}&maxDays=${maxDays}`
      );
      setCalibrations(response.data.data);
    } catch (err) {
      console.error("Erreur lors de la récupération des calibrations :", err.message);
      setError(t("calibration.loading_error"));
    }
  };

  const handleToggleDetails = () => {
    setShowDetails((prev) => !prev);
  };

  const handlePeriodChange = (e) => {
    const period = e.target.value;
    setSelectedPeriod(period);
    setMaxDays(periodOptions[period]);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value.toLowerCase());
  const handleLoadMore = () => setVisibleCount((prev) => prev + 10);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedCalibrations = [...calibrations].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let valueA = a[sortConfig.key];
    let valueB = b[sortConfig.key];

    if (sortConfig.key === "Eoutpout") {
      valueA = transducerNames[a.Eoutpout] || a.Eoutpout;
      valueB = transducerNames[b.Eoutpout] || b.Eoutpout;
    }

    if (typeof valueA === "string") {
      return sortConfig.direction === "asc"
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    } else {
      return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
    }
  });

  const filteredCalibrations = sortedCalibrations.filter(
    (calibration) =>
      (selectedTransducer === "All" || calibration.Eoutpout === selectedTransducer) &&
      [calibration.owner_login, calibration.cabname, transducerNames[calibration.Eoutpout] || ""]
        .some((field) => field?.toLowerCase().includes(searchTerm))
  );

  const visibleCalibrations = filteredCalibrations.slice(0, visibleCount);

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  return (
    <>
      <button onClick={handleToggleDetails} className={styles.loadMoreButton}>
        {showDetails ? t("calibration.hide_details") : t("calibration.show_center_details")}
      </button>

      {showDetails && (
        <div className={styles.container}>
          <h3>{t("calibration.upcoming_calibrations")}</h3>
          <div className={styles.filters}>
            <select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              className={styles.select}
            >
              {Object.entries(periodOptions).map(([key, value]) => (
                <option key={key} value={key}>
                  {t(`calibration.period_${key}`)}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder={t("calibration.search_placeholder_upcoming")}
              value={searchTerm}
              onChange={handleSearchChange}
              className={styles.searchInput}
            />
            <button type="button" onClick={fetchCalibrations} className={styles.fetchButton}>
              {t("calibration.filter")}
            </button>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => handleSort("owner_login")}>
                  {t("calibration.center_name")} {getSortIndicator("owner_login")}
                </th>
                <th onClick={() => handleSort("cabname")}>
                  {t("calibration.cabine_name")} {getSortIndicator("cabname")}
                </th>
                <th onClick={() => handleSort("Eoutpout")}>
                  {t("calibration.transducer_type")} {getSortIndicator("Eoutpout")}
                </th>
                <th onClick={() => handleSort("numberOfDays")}>
                  {t("calibration.days_remaining_header")} {getSortIndicator("numberOfDays")}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleCalibrations.map((calibration) => (
                <tr key={`${calibration.cabid}-${calibration.Eoutpout}`}>
                  <td>{calibration.owner_login}</td>
                  <td>{calibration.cabname}</td>
                  <td>{transducerNames[calibration.Eoutpout] || calibration.Eoutpout}</td>
                  <td>{calibration.numberOfDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {visibleCount < filteredCalibrations.length && (
            <button type="button" onClick={handleLoadMore} className={styles.loadMoreButton}>
              {t("calibration.load_more")}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default UpcomingCalibrations;
